/**
 * Inventory shelf label config + size resolution.
 * Used by API (PDF generation) and POS web (settings preview).
 */
import { z } from 'zod';

export const INVENTORY_LABEL_SIZE_PRESET_IDS = [
  '2x1',
  '2.25x1.25',
  '3x1',
  '1.5x1',
  'custom',
] as const;
export type InventoryLabelSizePresetId = (typeof INVENTORY_LABEL_SIZE_PRESET_IDS)[number];

const INV_PRESETS: Record<
  Exclude<InventoryLabelSizePresetId, 'custom'>,
  { widthIn: number; heightIn: number }
> = {
  '2x1': { widthIn: 2, heightIn: 1 },
  '2.25x1.25': { widthIn: 2.25, heightIn: 1.25 },
  '3x1': { widthIn: 3, heightIn: 1 },
  '1.5x1': { widthIn: 1.5, heightIn: 1 },
};

export const INVENTORY_LABEL_SIZE_PRESET_OPTIONS: ReadonlyArray<{
  id: InventoryLabelSizePresetId;
  label: string;
}> = [
  { id: '2x1', label: '2" × 1" (compact shelf)' },
  { id: '2.25x1.25', label: '2.25" × 1.25" (address)' },
  { id: '3x1', label: '3" × 1" (wide shelf)' },
  { id: '1.5x1', label: '1.5" × 1" (tiny)' },
  { id: 'custom', label: 'Custom width & height (inches)' },
];

export const INVENTORY_BARCODE_SOURCES = ['sku', 'upc', 'internal_id'] as const;
export type InventoryBarcodeSource = (typeof INVENTORY_BARCODE_SOURCES)[number];

export const INVENTORY_BARCODE_FORMATS = ['CODE128', 'EAN13', 'UPC_A'] as const;
export type InventoryBarcodeFormat = (typeof INVENTORY_BARCODE_FORMATS)[number];

export const INVENTORY_PRICE_SOURCES = ['retail', 'promotional', 'none'] as const;
export type InventoryPriceSource = (typeof INVENTORY_PRICE_SOURCES)[number];

export type InventoryLabelConfig = {
  size: InventoryLabelSizePresetId;
  labelWidthIn?: number;
  labelHeightIn?: number;
  showName: boolean;
  nameLines: number;
  showSku: boolean;
  showBarcode: boolean;
  barcodeSource: InventoryBarcodeSource;
  barcodeFormat: InventoryBarcodeFormat;
  showPrice: boolean;
  priceSource: InventoryPriceSource;
  showCost: boolean;
  showBinLocation: boolean;
  showCondition: boolean;
  showCompatibility: boolean;
  showStoreName: boolean;
  showDate: boolean;
  copies: number;
};

export const DEFAULT_INVENTORY_LABEL_CONFIG: InventoryLabelConfig = {
  size: '2x1',
  showName: true,
  nameLines: 2,
  showSku: true,
  showBarcode: true,
  barcodeSource: 'sku',
  barcodeFormat: 'CODE128',
  showPrice: true,
  priceSource: 'retail',
  showCost: false,
  showBinLocation: true,
  showCondition: false,
  showCompatibility: false,
  showStoreName: false,
  showDate: false,
  copies: 1,
};

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

export function resolveInventoryLabelDimensions(cfg: Partial<InventoryLabelConfig> | unknown): {
  widthIn: number;
  heightIn: number;
} {
  const o = cfg && typeof cfg === 'object' ? (cfg as Record<string, unknown>) : {};
  const sizeRaw = typeof o.size === 'string' ? o.size : '2x1';
  const size = INVENTORY_LABEL_SIZE_PRESET_IDS.includes(sizeRaw as InventoryLabelSizePresetId)
    ? (sizeRaw as InventoryLabelSizePresetId)
    : '2x1';

  if (size === 'custom') {
    const w =
      typeof o.labelWidthIn === 'number' && Number.isFinite(o.labelWidthIn) ? o.labelWidthIn : 2;
    const h =
      typeof o.labelHeightIn === 'number' && Number.isFinite(o.labelHeightIn) ? o.labelHeightIn : 1;
    return { widthIn: clamp(w, 0.5, 6), heightIn: clamp(h, 0.4, 6) };
  }

  return { ...INV_PRESETS[size] };
}

export function mergeInventoryLabelConfig(raw: unknown): InventoryLabelConfig {
  const base = { ...DEFAULT_INVENTORY_LABEL_CONFIG };
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Record<string, unknown>;

  const size =
    typeof o.size === 'string' &&
    INVENTORY_LABEL_SIZE_PRESET_IDS.includes(o.size as InventoryLabelSizePresetId)
      ? (o.size as InventoryLabelSizePresetId)
      : base.size;

  const barcodeSource =
    typeof o.barcodeSource === 'string' &&
    INVENTORY_BARCODE_SOURCES.includes(o.barcodeSource as InventoryBarcodeSource)
      ? (o.barcodeSource as InventoryBarcodeSource)
      : base.barcodeSource;

  const barcodeFormat =
    typeof o.barcodeFormat === 'string' &&
    INVENTORY_BARCODE_FORMATS.includes(o.barcodeFormat as InventoryBarcodeFormat)
      ? (o.barcodeFormat as InventoryBarcodeFormat)
      : base.barcodeFormat;

  const priceSource =
    typeof o.priceSource === 'string' &&
    INVENTORY_PRICE_SOURCES.includes(o.priceSource as InventoryPriceSource)
      ? (o.priceSource as InventoryPriceSource)
      : base.priceSource;

  const nameLines =
    typeof o.nameLines === 'number' && Number.isFinite(o.nameLines)
      ? clamp(Math.round(o.nameLines), 1, 4)
      : base.nameLines;

  const copies =
    typeof o.copies === 'number' && o.copies >= 1 ? clamp(Math.round(o.copies), 1, 99) : base.copies;

  const result: InventoryLabelConfig = {
    size,
    showName: o.showName !== false,
    nameLines,
    showSku: o.showSku !== false,
    showBarcode: o.showBarcode !== false,
    barcodeSource,
    barcodeFormat,
    showPrice: o.showPrice !== false,
    priceSource,
    showCost: o.showCost === true,
    showBinLocation: o.showBinLocation !== false,
    showCondition: o.showCondition === true,
    showCompatibility: o.showCompatibility === true,
    showStoreName: o.showStoreName === true,
    showDate: o.showDate === true,
    copies,
  };

  if (typeof o.labelWidthIn === 'number' && Number.isFinite(o.labelWidthIn)) {
    result.labelWidthIn = o.labelWidthIn;
  }
  if (typeof o.labelHeightIn === 'number' && Number.isFinite(o.labelHeightIn)) {
    result.labelHeightIn = o.labelHeightIn;
  }

  return result;
}

export const inventoryLabelConfigSchema = z
  .object({
    size: z.enum(INVENTORY_LABEL_SIZE_PRESET_IDS).optional(),
    labelWidthIn: z.coerce.number().min(0.5).max(6).optional(),
    labelHeightIn: z.coerce.number().min(0.4).max(6).optional(),
    showName: z.boolean().optional(),
    nameLines: z.coerce.number().int().min(1).max(4).optional(),
    showSku: z.boolean().optional(),
    showBarcode: z.boolean().optional(),
    barcodeSource: z.enum(INVENTORY_BARCODE_SOURCES).optional(),
    barcodeFormat: z.enum(INVENTORY_BARCODE_FORMATS).optional(),
    showPrice: z.boolean().optional(),
    priceSource: z.enum(INVENTORY_PRICE_SOURCES).optional(),
    showCost: z.boolean().optional(),
    showBinLocation: z.boolean().optional(),
    showCondition: z.boolean().optional(),
    showCompatibility: z.boolean().optional(),
    showStoreName: z.boolean().optional(),
    showDate: z.boolean().optional(),
    copies: z.coerce.number().int().min(1).max(99).optional(),
  })
  .strict();
